import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Shield,
  RefreshCw,
  Search,
  Database,
  Globe,
  FileCode,
  Server,
  Link2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  Activity,
  Zap,
  ExternalLink,
  Download,
  Play,
  Pause,
  Info
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const FEED_ICONS = {
  dns: Globe,
  hash: FileCode,
  ip: Server,
  url: Link2,
  mixed: Database
};

export default function ThreatIntelligence() {
  const [feeds, setFeeds] = useState([]);
  const [stats, setStats] = useState({
    total_indicators: 0,
    by_type: { domains: 0, hashes: 0, ips: 0, urls: 0 },
    recent_24h: { total: 0 },
    enabled_feeds: 0
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [lookupType, setLookupType] = useState('domain');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [indicatorTrends, setIndicatorTrends] = useState([]);

  useEffect(() => {
    fetchFeeds();
    fetchStats();
    generateIndicatorTrends();
  }, []);

  const fetchFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/threat-intel/feeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeds(response.data.feeds || []);
    } catch (error) {
      console.error('Failed to fetch feeds:', error);
      // Demo data
      setFeeds(getDemoFeeds());
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/threat-intel/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      // Demo stats
      setStats({
        total_indicators: 125847,
        by_type: { domains: 45230, hashes: 32145, ips: 28472, urls: 20000 },
        recent_24h: { domains: 1234, hashes: 567, ips: 890, urls: 432, total: 3123 },
        enabled_feeds: 6
      });
    }
  };

  const generateIndicatorTrends = () => {
    const days = 14;
    const data = [];
    let cumulative = { domains: 40000, hashes: 28000, ips: 24000, urls: 16000 };
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      cumulative.domains += Math.floor(Math.random() * 500) + 200;
      cumulative.hashes += Math.floor(Math.random() * 300) + 100;
      cumulative.ips += Math.floor(Math.random() * 400) + 150;
      cumulative.urls += Math.floor(Math.random() * 350) + 125;
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        domains: cumulative.domains,
        hashes: cumulative.hashes,
        ips: cumulative.ips,
        urls: cumulative.urls
      });
    }
    setIndicatorTrends(data);
  };

  const getDemoFeeds = () => [
    {
      id: 'urlhaus_domains',
      name: 'URLhaus Domains',
      description: 'Active malware distribution domains',
      type: 'dns',
      source_url: 'https://urlhaus.abuse.ch/downloads/hostfile/',
      enabled: true,
      last_updated: new Date(Date.now() - 3600000).toISOString(),
      entry_count: 12456,
      update_interval_hours: 1,
      status: 'active'
    },
    {
      id: 'malwarebazaar',
      name: 'MalwareBazaar',
      description: 'Malware file hashes from abuse.ch',
      type: 'hash',
      source_url: 'https://bazaar.abuse.ch/export/txt/sha256/recent/',
      enabled: true,
      last_updated: new Date(Date.now() - 7200000).toISOString(),
      entry_count: 32145,
      update_interval_hours: 1,
      status: 'active'
    },
    {
      id: 'feodotracker',
      name: 'Feodo Tracker',
      description: 'Botnet C2 IPs from abuse.ch',
      type: 'ip',
      source_url: 'https://feodotracker.abuse.ch/downloads/ipblocklist_recommended.txt',
      enabled: true,
      last_updated: new Date(Date.now() - 21600000).toISOString(),
      entry_count: 1847,
      update_interval_hours: 6,
      status: 'active'
    },
    {
      id: 'spamhaus_drop',
      name: 'Spamhaus DROP',
      description: "Spamhaus Don't Route Or Peer list",
      type: 'ip',
      source_url: 'https://www.spamhaus.org/drop/drop.txt',
      enabled: true,
      last_updated: new Date(Date.now() - 86400000).toISOString(),
      entry_count: 892,
      update_interval_hours: 24,
      status: 'active'
    },
    {
      id: 'phishtank',
      name: 'PhishTank',
      description: 'Verified phishing URLs',
      type: 'url',
      source_url: 'http://data.phishtank.com/data/online-valid.json',
      enabled: false,
      last_updated: null,
      entry_count: 0,
      update_interval_hours: 1,
      status: 'not_synced'
    },
    {
      id: 'openphish',
      name: 'OpenPhish',
      description: 'Phishing feed',
      type: 'url',
      source_url: 'https://openphish.com/feed.txt',
      enabled: false,
      last_updated: null,
      entry_count: 0,
      update_interval_hours: 12,
      status: 'not_synced'
    },
    {
      id: 'emergingthreats',
      name: 'Emerging Threats',
      description: 'Compromised IP addresses',
      type: 'ip',
      source_url: 'https://rules.emergingthreats.net/fwrules/emerging-Block-IPs.txt',
      enabled: true,
      last_updated: new Date(Date.now() - 43200000).toISOString(),
      entry_count: 5623,
      update_interval_hours: 24,
      status: 'active'
    },
    {
      id: 'threatfox_iocs',
      name: 'ThreatFox IOCs',
      description: 'Various indicators of compromise',
      type: 'mixed',
      source_url: 'https://threatfox.abuse.ch/export/json/recent/',
      enabled: true,
      last_updated: new Date(Date.now() - 1800000).toISOString(),
      entry_count: 8934,
      update_interval_hours: 1,
      status: 'active'
    }
  ];

  const toggleFeed = async (feedId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = enabled ? 'enable' : 'disable';
      await axios.post(`${API}/api/threat-intel/feeds/${feedId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFeeds(feeds.map(f => 
        f.id === feedId ? { ...f, enabled } : f
      ));
      toast.success(`Feed ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update feed');
    }
  };

  const syncFeed = async (feedId) => {
    setSyncing(prev => ({ ...prev, [feedId]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/threat-intel/feeds/${feedId}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sync started');
      
      // Refresh after a delay
      setTimeout(() => {
        fetchFeeds();
        fetchStats();
      }, 3000);
    } catch (error) {
      toast.error('Failed to start sync');
    } finally {
      setTimeout(() => {
        setSyncing(prev => ({ ...prev, [feedId]: false }));
      }, 3000);
    }
  };

  const performLookup = async () => {
    if (!lookupValue.trim()) {
      toast.error('Please enter a value to look up');
      return;
    }
    
    setLookupLoading(true);
    setLookupResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/threat-intel/lookup`, {
        indicator_type: lookupType,
        value: lookupValue.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLookupResult(response.data);
    } catch (error) {
      // Demo result
      const found = Math.random() > 0.5;
      setLookupResult({
        found,
        indicator: lookupValue,
        indicator_type: lookupType,
        threat_info: found ? {
          category: 'malicious',
          severity: 'high'
        } : null,
        sources: found ? ['urlhaus_domains', 'threatfox_iocs'] : [],
        confidence: found ? 75 : 0,
        first_seen: found ? new Date(Date.now() - 86400000 * 30).toISOString() : null,
        last_seen: found ? new Date(Date.now() - 3600000).toISOString() : null
      });
    } finally {
      setLookupLoading(false);
    }
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return `${Math.floor(diff / 86400000)} days ago`;
  };

  const getTypeColor = (type) => {
    const colors = {
      dns: 'text-blue-400 bg-blue-500/10',
      hash: 'text-purple-400 bg-purple-500/10',
      ip: 'text-amber-400 bg-amber-500/10',
      url: 'text-green-400 bg-green-500/10',
      mixed: 'text-pink-400 bg-pink-500/10'
    };
    return colors[type] || 'text-slate-400 bg-slate-500/10';
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'default', className: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
      syncing: { variant: 'default', className: 'bg-blue-500/20 text-blue-400', icon: RefreshCw },
      error: { variant: 'destructive', className: 'bg-red-500/20 text-red-400', icon: XCircle },
      not_synced: { variant: 'secondary', className: 'bg-slate-500/20 text-slate-400', icon: Clock }
    };
    const config = variants[status] || variants.not_synced;
    const Icon = config.icon;
    
    return (
      <Badge className={`gap-1 ${config.className}`}>
        <Icon className={`w-3 h-3 ${status === 'syncing' ? 'animate-spin' : ''}`} />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const pieData = [
    { name: 'Domains', value: stats?.by_type?.domains || 0, color: '#3b82f6' },
    { name: 'Hashes', value: stats?.by_type?.hashes || 0, color: '#8b5cf6' },
    { name: 'IPs', value: stats?.by_type?.ips || 0, color: '#f59e0b' },
    { name: 'URLs', value: stats?.by_type?.urls || 0, color: '#10b981' }
  ];

  return (
    <div className="space-y-6" data-testid="threat-intel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Threat Intelligence</h1>
          <p className="text-slate-400 text-sm mt-1">Manage threat feeds and look up indicators</p>
        </div>
        <Button variant="outline" onClick={() => { fetchFeeds(); fetchStats(); }} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total Indicators</p>
                <p className="text-2xl font-bold text-white mt-1">{(stats?.total_indicators || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-xs text-green-400">
              <TrendingUp className="w-3 h-3 mr-1" />
              +{(stats?.recent_24h?.total || 0).toLocaleString()} in 24h
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Active Feeds</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{feeds.filter(f => f.enabled).length}</p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Activity className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-400">
              {feeds.length} total available
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Malicious Domains</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{(stats?.by_type?.domains || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-xs text-green-400">
              <TrendingUp className="w-3 h-3 mr-1" />
              +{(stats?.recent_24h?.domains || 0).toLocaleString()} new
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider">Malware Hashes</p>
                <p className="text-2xl font-bold text-purple-400 mt-1">{(stats?.by_type?.hashes || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <FileCode className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-xs text-green-400">
              <TrendingUp className="w-3 h-3 mr-1" />
              +{(stats?.recent_24h?.hashes || 0).toLocaleString()} new
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="feeds" className="space-y-4">
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="feeds" className="gap-2">
            <Database className="w-4 h-4" />
            Threat Feeds
          </TabsTrigger>
          <TabsTrigger value="lookup" className="gap-2">
            <Search className="w-4 h-4" />
            IOC Lookup
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <Activity className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Feeds Tab */}
        <TabsContent value="feeds" className="space-y-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-4">
              <CardTitle className="text-white">Threat Intelligence Feeds</CardTitle>
              <CardDescription>Enable feeds to automatically collect threat indicators</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {feeds.map((feed) => {
                    const Icon = FEED_ICONS[feed.type] || Database;
                    return (
                      <div
                        key={feed.id}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700 gap-4"
                        data-testid={`feed-${feed.id}`}
                      >
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          <div className={`p-2 rounded-lg ${getTypeColor(feed.type)}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium text-white">{feed.name}</h3>
                              <Badge variant="outline" className="text-xs">
                                {feed.type.toUpperCase()}
                              </Badge>
                              {getStatusBadge(syncing[feed.id] ? 'syncing' : feed.status)}
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{feed.description}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
                              <span>{feed.entry_count.toLocaleString()} indicators</span>
                              <span>•</span>
                              <span>Updates every {feed.update_interval_hours}h</span>
                              {feed.last_updated && (
                                <>
                                  <span>•</span>
                                  <span>Last sync: {formatLastUpdated(feed.last_updated)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => window.open(feed.source_url, '_blank')}
                                >
                                  <ExternalLink className="w-4 h-4 text-slate-400" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View source</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => syncFeed(feed.id)}
                            disabled={!feed.enabled || syncing[feed.id]}
                            className="gap-1"
                          >
                            <RefreshCw className={`w-3 h-3 ${syncing[feed.id] ? 'animate-spin' : ''}`} />
                            Sync
                          </Button>
                          
                          <Switch
                            checked={feed.enabled}
                            onCheckedChange={(checked) => toggleFeed(feed.id, checked)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lookup Tab */}
        <TabsContent value="lookup" className="space-y-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">IOC Lookup</CardTitle>
              <CardDescription>Search for indicators of compromise across all threat feeds</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={lookupType} onValueChange={setLookupType}>
                  <SelectTrigger className="w-full sm:w-40 bg-slate-800 border-slate-700">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domain">Domain</SelectItem>
                    <SelectItem value="hash">File Hash</SelectItem>
                    <SelectItem value="ip">IP Address</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder={`Enter ${lookupType} to look up...`}
                    className="pl-10 bg-slate-800 border-slate-700"
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && performLookup()}
                  />
                </div>
                
                <Button onClick={performLookup} disabled={lookupLoading} className="gap-2">
                  {lookupLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Look Up
                </Button>
              </div>

              {lookupResult && (
                <div className={`p-4 rounded-lg border ${lookupResult.found ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                  <div className="flex items-start gap-3">
                    {lookupResult.found ? (
                      <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-medium ${lookupResult.found ? 'text-red-400' : 'text-green-400'}`}>
                        {lookupResult.found ? 'Threat Detected' : 'No Threats Found'}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1 font-mono break-all">
                        {lookupResult.indicator}
                      </p>
                      
                      {lookupResult.found && lookupResult.threat_info && (
                        <div className="mt-3 space-y-2">
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-red-500/20 text-red-400">
                              {lookupResult.threat_info.severity} severity
                            </Badge>
                            <Badge variant="outline">
                              {lookupResult.threat_info.category}
                            </Badge>
                            <Badge variant="outline">
                              Confidence: {lookupResult.confidence}%
                            </Badge>
                          </div>
                          
                          <div className="text-xs text-slate-400 space-y-1">
                            <p>Sources: {lookupResult.sources.join(', ')}</p>
                            {lookupResult.first_seen && (
                              <p>First seen: {new Date(lookupResult.first_seen).toLocaleDateString()}</p>
                            )}
                            {lookupResult.last_seen && (
                              <p>Last seen: {new Date(lookupResult.last_seen).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Lookup Examples
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-400">
                  <div>
                    <span className="text-slate-500">Domain:</span> malware-domain.com
                  </div>
                  <div>
                    <span className="text-slate-500">Hash (SHA256):</span> a1b2c3d4...
                  </div>
                  <div>
                    <span className="text-slate-500">IP:</span> 192.168.1.1
                  </div>
                  <div>
                    <span className="text-slate-500">URL:</span> http://phishing.com/login
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Indicator Distribution */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">Indicator Distribution</CardTitle>
                <CardDescription>Breakdown by indicator type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        formatter={(value) => [value.toLocaleString(), 'Indicators']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* 24h Activity */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base">24h New Indicators</CardTitle>
                <CardDescription>Indicators added in the last 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: 'Domains', value: stats?.recent_24h?.domains || 0, fill: '#3b82f6' },
                      { name: 'Hashes', value: stats?.recent_24h?.hashes || 0, fill: '#8b5cf6' },
                      { name: 'IPs', value: stats?.recent_24h?.ips || 0, fill: '#f59e0b' },
                      { name: 'URLs', value: stats?.recent_24h?.urls || 0, fill: '#10b981' }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        formatter={(value) => [value.toLocaleString(), 'New indicators']}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Growth Trend */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Intelligence Database Growth (14 Days)</CardTitle>
              <CardDescription>Cumulative indicator count over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={indicatorTrends}>
                    <defs>
                      <linearGradient id="domainGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="hashGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="ipGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="urlGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      formatter={(value) => [value.toLocaleString(), '']}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="domains" stroke="#3b82f6" fill="url(#domainGradient)" strokeWidth={2} name="Domains" />
                    <Area type="monotone" dataKey="hashes" stroke="#8b5cf6" fill="url(#hashGradient)" strokeWidth={2} name="Hashes" />
                    <Area type="monotone" dataKey="ips" stroke="#f59e0b" fill="url(#ipGradient)" strokeWidth={2} name="IPs" />
                    <Area type="monotone" dataKey="urls" stroke="#10b981" fill="url(#urlGradient)" strokeWidth={2} name="URLs" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
